import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { Button } from '@components/atoms/button';
import { CameraAccessDefault } from '@components/atoms/camera-access-default';
import { Icon } from '@components/atoms/icon';
import { Spinner } from '@components/atoms/spinner';
import { Typography } from '@components/atoms/typography';
import { CloseIcon } from '@components/icons/close-icon';
import {
    DocsVerificationBiometricTypeEnum,
    DocsVerificationInternalStepEnum,
    DocsVerificationLivenessEnum,
    DocsVerificationLivenessTagErrorEnum,
    OrganizationRoleEnum,
} from '@models/enums';
import type { FaceDetectResult, FileWithPreview } from '@models/types';
import { useCameraConstraints } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/hooks/use-camera-constraints';
import { useDemoData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { useFaceDetectorContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider';
import { useWidgetContext } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import type { ModelCheckError } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/hints/types';
import { Mask } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/selfie/liveness/step/liveness-process/mask';
import {
    FileErrorCodeEnum,
    LIVENESS_HINT_ERROR_CODES,
} from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/types/file-error-code-enum';
import type { LivenessBatchFrameMetadata } from '@modules/docs-verification-widget/api/requests/liveness-batch-metadata';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAppDispatch, useAppSelector } from '@store';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { useAppTheme } from '@theme';
import { getEnvVar } from '@utils/get-env-var';
import { base64ToFile, isAndroid, sleep } from '@utils/util';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { FACE_DETECTOR_LOADING_TIMEOUT } from '../../../../../docs-verification-steps/constants';

import { useLivenessDetectLoop } from './hooks/use-liveness-detect-loop';
import { useLivenessPrepareCountdown } from './hooks/use-liveness-prepare-countdown';
import { useLivenessTabVisibility } from './hooks/use-liveness-tab-visibility';
import { useResumeCameraStream } from './hooks/use-resume-camera-stream';
import { buildDeviceMetadata, buildFrameMetadata } from './utils/build-liveness-frame-metadata';
import type { PipelineResult } from './utils/face-pipeline';
import { resetFacePipelineState, runFacePipeline } from './utils/face-pipeline';
import type { ImageQualityResult } from './utils/image-quality-pipeline';
import { runImageQualityPipeline } from './utils/image-quality-pipeline';
import { validateCapturedLivenessFrame } from './utils/validate-captured-liveness-frame';
import { ErrorModal } from './error-modal';
import { Hints } from './hints';
import { ProcessLivenessContainer, WebCamContainer } from './styles';
import type { FacePositionsState, LivenessProps, LivenessUploadMetadata } from './types';
import { VerificationStatusEnum } from './types';

import '@tensorflow/tfjs-backend-cpu';
import '@tensorflow/tfjs-backend-webgl';

const defaultFacePositions = {
    [DocsVerificationLivenessEnum.CENTER]: false,
    [DocsVerificationLivenessEnum.UP]: false,
    [DocsVerificationLivenessEnum.RIGHT]: false,
    [DocsVerificationLivenessEnum.DOWN]: false,
    [DocsVerificationLivenessEnum.LEFT]: false,
};
const SCAN_INTERVAL_MS = 200;
const ERROR_PAUSE_DELAY = 5000;
const HINT_ERROR_PAUSE_DELAY = 4000;

const ERROR_BADGE_THRESHOLD = 4000 / SCAN_INTERVAL_MS;
const ERROR_MODAL_THRESHOLD = 8000 / SCAN_INTERVAL_MS;

const SUCCESS_DETECT_COUNT = 4;
const MIN_SUCCESS_FRAME_SPACING_MS = 425;

export const LivenessProcess: React.FC<
    LivenessProps & {
        onSubmit: (isFailedVerification?: boolean) => Promise<boolean>;
        onClose: () => void;
    }
> = ({ titleCameraNotAllowed, onFileChange, onSubmit, onClose, className, errorProceedText }) => {
    const framesToUpload = useRef<
        Array<{ file: FileWithPreview; metadata: LivenessBatchFrameMetadata }>
    >([]);
    const sessionStartTs = useRef<number | null>(null);
    const lastPipelineResultRef = useRef<PipelineResult | null>(null);
    const lastImageQualityResultRef = useRef<ImageQualityResult | null>(null);
    const iqaFailCountsRef = useRef<Record<string, number>>({});
    const mediapipeFailCountsRef = useRef<Record<string, number>>({});
    const theme = useAppTheme();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const { scrollTop } = useWidgetContext();
    const { uploadFileRef } = useDemoData();
    const { cameraConstraints } = useCameraConstraints();
    const {
        tfLiteLandmarkModel,
        tfLiteModel,
        imageQualityModel,
        isTfliteScriptReady,
        hasFaceDetectorLoadError,
        refetchTfliteScripts,
        refetchFaceModels,
    } = useFaceDetectorContext();

    const faceModelsRef = useRef({
        tfLiteModel,
        tfLiteLandmarkModel,
        imageQualityModel,
    });
    const isTfliteScriptReadyRef = useRef(isTfliteScriptReady);

    useEffect(() => {
        faceModelsRef.current = {
            tfLiteModel,
            tfLiteLandmarkModel,
            imageQualityModel,
        };
    }, [tfLiteModel, tfLiteLandmarkModel, imageQualityModel]);

    useEffect(() => {
        isTfliteScriptReadyRef.current = isTfliteScriptReady;
    }, [isTfliteScriptReady]);

    const hasAllFaceModels = useCallback(() => {
        const {
            tfLiteModel: detector,
            tfLiteLandmarkModel: landmark,
            imageQualityModel: iqa,
        } = faceModelsRef.current;

        return Boolean(detector && landmark && iqa);
    }, []);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const webContainerRef = useRef<HTMLDivElement | null>(null);
    const faceDetectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dispatch = useAppDispatch();
    const userRole = useAppSelector(
        (state) => state?.dashboard?.session?.organizationInfo?.organization_role,
    ) as OrganizationRoleEnum;
    const { t } = useTranslation();
    const [isCameraNotAllowed, setIsCameraNotAllowed] = useState(false);
    const [currentStep, setCurrentStep] = useState<DocsVerificationInternalStepEnum>(
        DocsVerificationInternalStepEnum.CAMERA_ACCESS,
    );
    const webcamRef = useRef<Webcam>(null);
    const isInitialized = useRef(false);
    const isDone = useRef(false);
    const hasRequestedFaceDetectorReload = useRef(false);
    const facePositions = useRef<FacePositionsState>(defaultFacePositions);
    const failedChecksCount = useRef(0);
    const faceCount = useRef(1);
    const [status, setStatus] = useState<VerificationStatusEnum | null>(null);
    const isPaused = useRef(false);
    const isDetectInProgress = useRef(false);
    const detectCount = useRef(0);
    const successDetectCount = useRef(0);
    const lastSuccessFrameTs = useRef(0);
    const [faceDetectResult, setFaceDetectResult] = useState<FaceDetectResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isMaskReady, setIsMaskReady] = useState(false);
    const [modelCheckError, setModelCheckError] = useState<ModelCheckError | undefined>(undefined);
    const [fileErrorCode, setFileErrorCode] = useState<number | undefined>(undefined);
    const [errorModal, setErrorModal] = useState<ModelCheckError | undefined>(undefined);
    const prevError = useRef<ModelCheckError | undefined>(undefined);
    const errorCount = useRef<number>(0);

    useEffect(() => {
        if (hasFaceDetectorLoadError) {
            setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);
        }
    }, [hasFaceDetectorLoadError]);

    const maskReady = useCallback(() => {
        setIsMaskReady(true);
    }, []);

    const bumpFailCount = useCallback((bucket: 'iqa' | 'mediapipe', reason: string) => {
        const target = bucket === 'iqa' ? iqaFailCountsRef : mediapipeFailCountsRef;

        target.current[reason] = (target.current[reason] ?? 0) + 1;
    }, []);

    const resetCaptureSessionStats = useCallback(() => {
        framesToUpload.current = [];
        detectCount.current = 0;
        successDetectCount.current = 0;
        lastSuccessFrameTs.current = 0;
        iqaFailCountsRef.current = {};
        mediapipeFailCountsRef.current = {};
        sessionStartTs.current = performance.now();
    }, []);

    const buildUploadMetadata = useCallback(
        (options?: { quantityFallback?: boolean }): LivenessUploadMetadata => {
            const totalCaptureTimeMs =
                sessionStartTs.current == null
                    ? undefined
                    : Math.round(performance.now() - sessionStartTs.current);

            return {
                frames: framesToUpload.current.map((frame, index) => ({
                    ...frame.metadata,
                    frame_id: frame.metadata.frame_id || `frame${index + 1}`,
                })),
                capture_config: {
                    target_min: SUCCESS_DETECT_COUNT,
                    target_max: SUCCESS_DETECT_COUNT,
                    min_time_between_accepted_ms: MIN_SUCCESS_FRAME_SPACING_MS,
                    fallback_min_send: SUCCESS_DETECT_COUNT,
                },
                capture_stats: {
                    accepted_frames_count: framesToUpload.current.length,
                    total_frames_evaluated: detectCount.current,
                    total_capture_time_ms: totalCaptureTimeMs,
                    iqa_fail_counts: { ...iqaFailCountsRef.current },
                    mediapipe_fail_counts: { ...mediapipeFailCountsRef.current },
                },
                ...(options?.quantityFallback
                    ? {
                          fallback_info: {
                              quantity_fallback: true,
                              accepted_count: framesToUpload.current.length,
                              low_quality_fallback: false,
                          },
                      }
                    : {}),
            };
        },
        [],
    );

    const getWebcamFile = useCallback(async () => {
        const imageSrc = webcamRef.current?.getScreenshot(
            matchesMobileSm && !isAndroid() && !theme.isLandscape
                ? { width: 800, height: 600 }
                : undefined,
        );

        let fileWithPreview = (await base64ToFile(
            imageSrc as string,
            `photo_${uuidv4()}.jpeg`,
        )) as FileWithPreview;

        fileWithPreview = Object.assign(fileWithPreview, {
            preview: imageSrc,
        });

        return fileWithPreview;
    }, [matchesMobileSm, theme.isLandscape]);

    const capture = useCallback(
        async ({
            tag,
            detectResult,
            isIgnoreCheck,
            file,
            metadata,
        }: {
            documentType: DocsVerificationBiometricTypeEnum;
            tag?: DocsVerificationLivenessTagErrorEnum;
            detectResult?: FaceDetectResult;
            isIgnoreCheck: boolean;
            file: FileWithPreview;
            metadata?: LivenessUploadMetadata;
        }) => {
            if (onFileChange) {
                if (isIgnoreCheck) {
                    await onFileChange(file, tag, detectResult);
                } else {
                    isPaused.current = true;
                    await sleep(500);
                    setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                    const [verificationFileState] = await Promise.all([
                        onFileChange(
                            framesToUpload.current.map((frame) => frame.file),
                            tag,
                            detectResult,
                            metadata,
                        ),
                        sleep(5000),
                    ]);

                    framesToUpload.current = [];

                    if (verificationFileState.status.success) {
                        isPaused.current = true;
                        setStatus(VerificationStatusEnum.SUCCESS);
                        await sleep(1500);
                        const isSuccess = await onSubmit(
                            verificationFileState.status.isFailedVerification,
                        );

                        if (isSuccess) {
                            isDone.current = true;
                        } else {
                            isPaused.current = false;
                            setStatus(VerificationStatusEnum.ALIGN_FACE);
                        }
                    } else {
                        const statusMessage = verificationFileState.status.message ?? '';
                        const statusCode = Number(verificationFileState.status.code);
                        const hintErrorCode = LIVENESS_HINT_ERROR_CODES.find(
                            (code) => statusCode === code || statusMessage.includes(String(code)),
                        );

                        if (hintErrorCode !== undefined) {
                            isPaused.current = true;
                            setTimeout(() => {
                                isPaused.current = false;
                            }, HINT_ERROR_PAUSE_DELAY);
                            resetCaptureSessionStats();
                            facePositions.current = defaultFacePositions;
                            setFileErrorCode(hintErrorCode);
                            setStatus(VerificationStatusEnum.FILE_ERROR);

                            return;
                        }

                        if (
                            statusMessage.includes(
                                String(FileErrorCodeEnum.POOR_LIGHTING_CONDITIONS),
                            )
                        ) {
                            isPaused.current = true;
                            setTimeout(() => {
                                isPaused.current = false;
                            }, ERROR_PAUSE_DELAY);
                            resetCaptureSessionStats();
                            facePositions.current = defaultFacePositions;
                            setStatus(VerificationStatusEnum.POOR_LIGHTING);

                            return;
                        }
                        failedChecksCount.current += 1;
                        facePositions.current = defaultFacePositions;
                        setStatus(VerificationStatusEnum.ALIGN_FACE);
                        resetCaptureSessionStats();
                        isPaused.current = false;
                    }
                }
            }
        },
        [onFileChange, onSubmit, resetCaptureSessionStats],
    );

    const tryLoadFacemesh = useCallback(
        async (isSilent?: boolean) => {
            try {
                if (hasAllFaceModels()) {
                    return true;
                }

                setStatus(
                    isSilent
                        ? VerificationStatusEnum.FACE_DETECTOR_LOADING_SILENT
                        : VerificationStatusEnum.FACE_DETECTOR_LOADING,
                );

                if (hasRequestedFaceDetectorReload.current) {
                    faceModelsRef.current = {
                        tfLiteModel: null,
                        tfLiteLandmarkModel: null,
                        imageQualityModel: null,
                    };

                    if (isTfliteScriptReadyRef.current) {
                        refetchFaceModels();
                    } else {
                        isTfliteScriptReadyRef.current = false;
                        refetchTfliteScripts();
                    }
                }

                hasRequestedFaceDetectorReload.current = true;
                const waitUntilReady = (): Promise<boolean> => {
                    const deadline = Date.now() + FACE_DETECTOR_LOADING_TIMEOUT;

                    const poll = (): Promise<boolean> => {
                        if (hasAllFaceModels()) {
                            return Promise.resolve(true);
                        }

                        if (Date.now() >= deadline) {
                            return Promise.resolve(false);
                        }

                        return sleep(100).then(poll);
                    };

                    return poll();
                };

                const isReady = isSilent
                    ? await waitUntilReady()
                    : (
                          await Promise.all([
                              waitUntilReady(),
                              sleep(FACE_DETECTOR_LOADING_TIMEOUT),
                          ])
                      )[0];

                if (!isReady) {
                    setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

                    return false;
                }

                return true;
            } catch {
                setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

                return false;
            }
        },
        [hasAllFaceModels, refetchTfliteScripts, refetchFaceModels],
    );

    // eslint-disable-next-line complexity
    const detect = useCallback(async (): Promise<FaceDetectResult | undefined> => {
        if (
            typeof webcamRef.current !== 'undefined' &&
            webcamRef.current !== null &&
            webcamRef?.current?.video?.readyState === 4
        ) {
            const detector = tfLiteModel;
            const landmark = tfLiteLandmarkModel;
            const iqaModel = imageQualityModel;

            if (!detector || !landmark || !iqaModel) {
                return undefined;
            }

            const { video } = webcamRef.current;
            const { videoWidth } = webcamRef.current?.video;
            const { videoHeight } = webcamRef.current?.video;

            webcamRef.current.video.width = videoWidth || 720;
            webcamRef.current.video.height = videoHeight || 480;

            let pipelineResult: PipelineResult | null = null;
            let imageQualityResult: ImageQualityResult | null = null;

            try {
                pipelineResult = runFacePipeline(detector, landmark, video);

                const geometryAligned = Boolean(
                    pipelineResult?.hasFace &&
                    pipelineResult.box &&
                    (pipelineResult.isCentered ?? false) &&
                    (pipelineResult.isPoseOk ?? false) &&
                    !pipelineResult.eyesClosed &&
                    !pipelineResult.isLowQuantity,
                );

                imageQualityResult =
                    geometryAligned && pipelineResult?.box
                        ? runImageQualityPipeline(
                              iqaModel,
                              video,
                              pipelineResult.landmarkBox ?? pipelineResult.box,
                          )
                        : null;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Face pipeline error:', e);
                await tryLoadFacemesh(true);
            }

            detectCount.current += 1;

            if (sessionStartTs.current == null) {
                sessionStartTs.current = performance.now();
            }

            lastPipelineResultRef.current = pipelineResult;
            lastImageQualityResultRef.current = imageQualityResult;

            if (
                pipelineResult &&
                pipelineResult.faceCount > 1 &&
                pipelineResult.faceCount !== faceCount.current
            ) {
                faceCount.current = pipelineResult.faceCount;
                await capture({
                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO_EXTRA,
                    tag: DocsVerificationLivenessTagErrorEnum.MULTIPARTY,
                    isIgnoreCheck: true,
                    file: await getWebcamFile(),
                });
            }

            if (pipelineResult?.hasFace && pipelineResult?.box) {
                const [x1, y1, x2, y2] = pipelineResult.box;
                const boxW = x2 - x1;
                const boxH = y2 - y1;
                const facePercent = ((boxW * boxH) / (videoWidth * videoHeight)) * 100;
                const isCenter = pipelineResult.isCentered ?? false;

                const isLookingStraight =
                    (pipelineResult.isPoseOk ?? false) && !pipelineResult.eyesClosed;

                const isLowQuality = Boolean(pipelineResult?.isLowQuantity);

                const isSuccess =
                    isCenter &&
                    isLookingStraight &&
                    !isLowQuality &&
                    !!imageQualityResult &&
                    imageQualityResult.failedChecks.length === 0;

                const isBlur = imageQualityResult?.failedChecks.includes('blur');
                const isLowQuantity = isLowQuality || isBlur;
                const isHighIllumination =
                    imageQualityResult?.failedChecks.includes('high_illumination');
                const isLowIllumination =
                    imageQualityResult?.failedChecks.includes('low_illumination');
                const isEyesClosed = pipelineResult.eyesClosed;

                let error: ModelCheckError | undefined;

                if (isLowQuantity) {
                    error = 'lowQuantity';
                } else if (isLowIllumination) {
                    error = 'lowIllumination';
                } else if (isHighIllumination) {
                    error = 'highIllumination';
                } else if (isEyesClosed) {
                    error = 'eyeClosed';
                }

                if (!isSuccess) {
                    const iqaReasonMap: Record<string, string> = {
                        high_illumination: 'light',
                        low_illumination: 'dark',
                        low_resolution: 'resolution',
                    };

                    imageQualityResult?.failedChecks.forEach((reason) => {
                        bumpFailCount('iqa', iqaReasonMap[reason] ?? reason);
                    });

                    if (!pipelineResult.hasFace) {
                        bumpFailCount('mediapipe', 'no_face');
                    } else if (!isCenter) {
                        bumpFailCount('mediapipe', 'not_centered');
                    } else if (!(pipelineResult.isPoseOk ?? false)) {
                        bumpFailCount('mediapipe', 'rotation');
                    } else if (isEyesClosed) {
                        bumpFailCount('mediapipe', 'closed_eyes');
                    } else if (isLowQuality) {
                        bumpFailCount('mediapipe', 'low_quantity');
                    }
                }

                if (error && prevError.current === error) {
                    errorCount.current += 1;
                    if (errorCount.current === ERROR_BADGE_THRESHOLD) {
                        setModelCheckError(error);
                    }
                    if (error && errorCount.current === ERROR_MODAL_THRESHOLD) {
                        isPaused.current = true;
                        prevError.current = undefined;
                        errorCount.current = 0;
                        setErrorModal(error);
                        setModelCheckError(undefined);
                    }
                } else if (error) {
                    isPaused.current = false;
                    setModelCheckError(undefined);
                    errorCount.current = 0;
                    prevError.current = error;
                } else {
                    isPaused.current = false;
                    setModelCheckError(undefined);
                    errorCount.current = 0;
                    prevError.current = undefined;
                }

                return {
                    error,
                    isLowQuantity,
                    isHighIllumination,
                    isLowIllumination,
                    isBlur,
                    isSuccess,
                    isCenter,
                    isLookingStraight,
                    isEyesClosed,
                    isFaceFound: pipelineResult.hasFace,
                    facePercent: {
                        value: facePercent,
                        hint: imageQualityResult?.failedChecks[0],
                    },
                };
            }

            setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
            bumpFailCount('mediapipe', 'no_face');

            return {
                isLowQuantity: false,
                isHighIllumination: false,
                isLowIllumination: false,
                isBlur: false,
                isSuccess: false,
                isCenter: false,
                isFaceFound: false,
                isLookingStraight: false,
                isEyesClosed: false,
                facePercent: { value: 0, hint: undefined },
            };
        }

        return undefined;
    }, [
        capture,
        tryLoadFacemesh,
        getWebcamFile,
        tfLiteModel,
        tfLiteLandmarkModel,
        imageQualityModel,
        bumpFailCount,
    ]);

    const { prepareSeconds, prepareSecondsRef, prepare, skipPrepareCountdown, abortPrepare } =
        useLivenessPrepareCountdown({
            webcamRef,
            isPaused,
            isMaskReady,
            isTfliteScriptReady,
            areFaceModelsLoaded: Boolean(tfLiteModel && tfLiteLandmarkModel && imageQualityModel),
            hasFaceDetectorLoadError,
            status,
            setStatus,
            detect,
        });

    const { resumeCameraStream } = useResumeCameraStream(webcamRef);

    const checkFaceDetectedResult = useCallback(
        async (res: FaceDetectResult) => {
            if (Object.values(res)) {
                if (res.isFaceFound) {
                    setStatus(VerificationStatusEnum.FACE_FOUND);
                    if (res.isSuccess) {
                        const now = performance.now();
                        const spacedEnough =
                            now - lastSuccessFrameTs.current >= MIN_SUCCESS_FRAME_SPACING_MS;

                        if (spacedEnough) {
                            lastSuccessFrameTs.current = now;

                            const detector = faceModelsRef.current.tfLiteModel;
                            const landmark = faceModelsRef.current.tfLiteLandmarkModel;
                            const iqaModel = faceModelsRef.current.imageQualityModel;

                            if (!detector || !landmark || !iqaModel) {
                                return;
                            }

                            const file = await getWebcamFile();
                            const validated = await validateCapturedLivenessFrame({
                                file,
                                detectorModel: detector,
                                landmarkModel: landmark,
                                imageQualityModel: iqaModel,
                            });

                            if (!validated) {
                                return;
                            }

                            const frameIndex = framesToUpload.current.length;

                            if (frameIndex < SUCCESS_DETECT_COUNT) {
                                framesToUpload.current.push({
                                    file,
                                    metadata: buildFrameMetadata({
                                        frameId: `frame${frameIndex + 1}`,
                                        timestamp: Math.round(
                                            now - (sessionStartTs.current ?? now),
                                        ),
                                        pipelineResult: validated.pipelineResult,
                                        imageQualityResult: validated.imageQualityResult,
                                    }),
                                });
                                successDetectCount.current += 1;
                            }

                            if (successDetectCount.current >= SUCCESS_DETECT_COUNT) {
                                isPaused.current = true;
                                if (prepareSecondsRef.current) {
                                    return;
                                }
                                await capture({
                                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                                    detectResult: res,
                                    isIgnoreCheck: false,
                                    file,
                                    metadata: buildUploadMetadata(),
                                });
                            }
                        }
                    }
                } else {
                    setStatus(VerificationStatusEnum.FACE_NOT_FOUND);
                }
            }

            if (!isPaused.current) {
                setFaceDetectResult(res);
            }
        },
        [buildUploadMetadata, capture, getWebcamFile, prepareSecondsRef],
    );

    useEffect(() => {
        navigator.mediaDevices
            .getUserMedia(cameraConstraints)
            .then((stream) => {
                const tracks = stream.getTracks();

                tracks?.forEach((track) => {
                    track.stop();
                });
                setCurrentStep(DocsVerificationInternalStepEnum.SCANNING);
            })
            .catch(() => {
                setIsCameraNotAllowed(true);
            });
    }, [cameraConstraints]);

    const resetRepeatedErrorState = useCallback(() => {
        errorCount.current = 0;
        prevError.current = undefined;
        setModelCheckError(undefined);
        resetCaptureSessionStats();
    }, [resetCaptureSessionStats]);

    const handleTryAgain = useCallback(async () => {
        setErrorModal(undefined);
        resetRepeatedErrorState();
        setStatus(VerificationStatusEnum.PREPARING);
        await prepare();
        setStatus(VerificationStatusEnum.READY);
    }, [prepare, resetRepeatedErrorState]);

    const handleCheckRecommendations = useCallback(() => {
        setErrorModal(undefined);
        resetRepeatedErrorState();
        dispatch(docsVerificationSlice.actions.setIsShowRequirements(true));
    }, [dispatch, resetRepeatedErrorState]);

    const { startDetectingRef, abortDetectLoop } = useLivenessDetectLoop({
        isDone,
        isPaused,
        isDetectInProgress,
        faceDetectTimeout,
        detect,
        checkFaceDetectedResult,
        errorProceedText,
    });

    useLivenessTabVisibility({
        isDone,
        abortPrepare,
        abortDetectLoop,
        skipPrepareCountdown,
        resumeCameraStream,
        onTabVisible: resetFacePipelineState,
        startDetectingRef,
    });

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const loadFaceMash = async (): Promise<boolean> => {
                if (!hasAllFaceModels()) {
                    const isLoaded = await tryLoadFacemesh();

                    if (!hasAllFaceModels() || !isLoaded) {
                        return false;
                    }
                }

                setStatus(VerificationStatusEnum.PREPARING);
                setTimeout(() => {
                    scrollTop();
                });

                return true;
            };

            const initialize = async () => {
                const isLoaded = await loadFaceMash();

                if (!isLoaded) {
                    setStatus(VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR);

                    return;
                }

                await sleep(1500);
                resetFacePipelineState();
                startDetectingRef.current();
            };

            initialize().catch(() => {});
        }

        return () => {
            abortDetectLoop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMakePhotoClick = useCallback(async () => {
        const file = await getWebcamFile();
        const now = performance.now();

        if (sessionStartTs.current == null) {
            sessionStartTs.current = now;
        }

        framesToUpload.current = Array.from({ length: SUCCESS_DETECT_COUNT }, (_, index) => ({
            file,
            metadata: buildFrameMetadata({
                frameId: `frame${index + 1}`,
                timestamp: Math.round(now - (sessionStartTs.current ?? now)),
                pipelineResult: lastPipelineResultRef.current,
                imageQualityResult: lastImageQualityResultRef.current,
            }),
        }));

        await capture({
            documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
            detectResult: faceDetectResult as FaceDetectResult,
            isIgnoreCheck: false,
            file,
            metadata: buildUploadMetadata({ quantityFallback: true }),
        });
    }, [buildUploadMetadata, capture, faceDetectResult, getWebcamFile]);

    const handleUploadFileClick = useCallback(async () => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                isPaused.current = true;
                setStatus(VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS);
                const file = event.target.files[0] as FileWithPreview;
                const now = performance.now();

                if (sessionStartTs.current == null) {
                    sessionStartTs.current = now;
                }

                file.preview = URL.createObjectURL(file);
                framesToUpload.current = Array.from(
                    { length: SUCCESS_DETECT_COUNT },
                    (_, index) => ({
                        file,
                        metadata: {
                            frame_id: `frame${index + 1}`,
                            timestamp: Math.round(now - (sessionStartTs.current ?? now)),
                            device_metadata: buildDeviceMetadata(),
                        },
                    }),
                );

                await capture({
                    documentType: DocsVerificationBiometricTypeEnum.LIVENESS_PHOTO,
                    detectResult: faceDetectResult as FaceDetectResult,
                    isIgnoreCheck: false,
                    file,
                    metadata: buildUploadMetadata({ quantityFallback: true }),
                });
            }
        },
        [buildUploadMetadata, capture, faceDetectResult],
    );

    useEffect(() => {
        if (uploadFileRef) {
            uploadFileRef.current = handleFileChange;
        }
    }, [handleFileChange, uploadFileRef]);

    useEffect(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const isLoading =
        status !== VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR &&
        (currentStep === DocsVerificationInternalStepEnum.CAMERA_ACCESS ||
            (currentStep === DocsVerificationInternalStepEnum.SCANNING && !isMaskReady) ||
            status === VerificationStatusEnum.FACE_DETECTOR_LOADING);

    return (
        <ProcessLivenessContainer ref={containerRef} className={className}>
            {isCameraNotAllowed ? (
                <CameraAccessDefault title={titleCameraNotAllowed} onClose={onClose} />
            ) : (
                <React.Fragment>
                    {isLoading && (
                        <Spinner
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                            }}
                        />
                    )}
                    {currentStep === DocsVerificationInternalStepEnum.SCANNING && (
                        <React.Fragment>
                            {!!prepareSeconds && isMaskReady && (
                                <Typography
                                    variant='text-bold'
                                    color='primary-a7'
                                    sx={{
                                        fontSize: '36px !important',
                                        position: 'absolute',
                                        top: 'calc(50% - 20px)',
                                        left: 'calc(50% - 12px)',
                                        zIndex: 10,
                                    }}
                                >
                                    {prepareSeconds}
                                </Typography>
                            )}
                            <Hints
                                faceDetectResult={faceDetectResult}
                                verificationStatus={status}
                                prepareSeconds={prepareSeconds}
                                tryLoadFacemesh={tryLoadFacemesh}
                                errorProceedText={errorProceedText}
                                isPaused={isPaused.current}
                                onContinue={async () => {
                                    setStatus(VerificationStatusEnum.PREPARING);
                                    await prepare();
                                    setStatus(VerificationStatusEnum.READY);
                                }}
                                currentError={modelCheckError}
                                fileErrorCode={fileErrorCode}
                                isMaskReady={isMaskReady}
                                video={webcamRef?.current?.video}
                            />
                            <WebCamContainer
                                ref={webContainerRef}
                                className={cn({
                                    'mask-ready': isMaskReady,
                                    'success-fade': status === VerificationStatusEnum.SUCCESS,
                                })}
                            >
                                {status !== VerificationStatusEnum.FACE_DETECTOR_LOADING_ERROR && (
                                    <React.Fragment>
                                        <Webcam
                                            ref={webcamRef}
                                            audio={false}
                                            mirrored={true}
                                            videoConstraints={cameraConstraints.video}
                                            screenshotFormat='image/jpeg'
                                            screenshotQuality={1}
                                            forceScreenshotSourceSize={true}
                                            minScreenshotHeight={2160}
                                            minScreenshotWidth={4096}
                                        />
                                        <Mask
                                            isLoading={
                                                status ===
                                                    VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS ||
                                                status === VerificationStatusEnum.SUCCESS
                                            }
                                            isError={
                                                (status === VerificationStatusEnum.FACE_NOT_FOUND &&
                                                    !prepareSeconds) ||
                                                !!modelCheckError
                                            }
                                            video={webcamRef?.current?.video}
                                            onReady={maskReady}
                                        />
                                    </React.Fragment>
                                )}
                            </WebCamContainer>
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
            {!matchesMobileSm && !isCameraNotAllowed && (
                <Icon
                    icon={<CloseIcon />}
                    $clickable={true}
                    className='close-icon'
                    onClick={onClose}
                />
            )}
            <div className='liveness-process-actions'>
                {(getEnvVar('REACT_APP_IS_SDK_DEBUG') ||
                    userRole === OrganizationRoleEnum.ADMIN) && (
                    <div className='admin-actions'>
                        <Button
                            variant='primary'
                            className='take-photo-button'
                            onClick={handleMakePhotoClick}
                            size='xl'
                        >
                            {t('docVerificationWidget.camera.takePhoto')}
                        </Button>
                        <input
                            ref={fileInputRef}
                            type='file'
                            name='image'
                            accept='image/*'
                            multiple={false}
                            onChange={handleFileChange}
                            className='hidden'
                        />
                        <Button
                            variant='primary'
                            onClick={handleUploadFileClick}
                            size='xl'
                            loading={status === VerificationStatusEnum.CHECK_FACE_BY_BE_IN_PROGRESS}
                        >
                            {t('common.uploadFile')}
                        </Button>
                    </div>
                )}
            </div>
            <ErrorModal
                error={errorModal}
                onClose={handleTryAgain}
                onTryAgain={handleTryAgain}
                onCheckRecommendations={handleCheckRecommendations}
            />
        </ProcessLivenessContainer>
    );
};
